

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, BatteryCharging, Zap, CreditCard } from 'lucide-react';
import ErrorBoundary from '../ErrorBoundary';
import { formatPrice, formatDuration, formatEnergy } from '@/utils/formatting';
import { InterestButton } from './InterestButton';

interface BookingSidebarProps {
  price: number;
  powerOutput: number;
  chargerType: string;
  available: boolean;
  stationId: string;
}

/**
 * Booking sidebar component that displays charging details and pricing information
 */
const BookingSidebar = ({ 
  price = 0, 
  powerOutput = 0, 
  chargerType = '', 
  available = false,
  stationId = '' 
}: BookingSidebarProps) => {
  const navigate = useNavigate();

  // Ensure we have valid numerical values
  const validPrice = typeof price === 'number' && !isNaN(price) && price >= 0 ? price : 0;
  const validPowerOutput = typeof powerOutput === 'number' && !isNaN(powerOutput) && powerOutput > 0 ? powerOutput : 1;
  
  // Calculate charging time for a standard 60kWh battery (hours)
  const chargingTime = validPowerOutput > 0 ? (60 / validPowerOutput) : 0;
  // Calculate total cost for a full charge (60kWh)
  const totalCost = 60 * validPrice;

  // Handle booking button click
  const handleBookClick = () => {
    if (stationId) {
      navigate(`/book/${stationId}`);
    }
  };

  return (
    <ErrorBoundary>
      <Card className="sticky top-24 transition-all duration-300 hover:shadow-md">
        <CardContent className="p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold">${formatPrice(validPrice)}</h3>
              <p className="text-muted-foreground">per kWh</p>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Charging Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="flex items-center">
                    <Zap className="h-4 w-4 mr-1.5 text-blue-500" />
                    Charger Type:
                  </span>
                  <span className="font-medium capitalize">{chargerType || 'Standard'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center">
                    <BatteryCharging className="h-4 w-4 mr-1.5 text-blue-500" />
                    Power Output:
                  </span>
                  <span className="font-medium">{validPowerOutput} kW</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Estimated Full Charge (60kWh):</span>
                  <span className="font-medium">
                    {formatDuration(chargingTime)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center">
                    <CreditCard className="h-4 w-4 mr-1.5 text-blue-500" />
                    Estimated Cost (60kWh):
                  </span>
                  <span className="font-medium">
                    ${formatPrice(totalCost)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex justify-between items-center text-sm mb-2">
                <span>Charging Speed</span>
                <span className="font-medium">{formatEnergy(validPowerOutput)} / hour</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ 
                    width: `${Math.min(100, (validPowerOutput / 150) * 100)}%`,
                    backgroundColor: validPowerOutput > 100 ? '#3b82f6' : 
                                    validPowerOutput > 50 ? '#6366f1' : 
                                    validPowerOutput > 20 ? '#8b5cf6' : '#a855f7'
                  }}
                ></div>
              </div>
            </div>
            
            <div className="space-y-3">
              {available && stationId ? (
                <Button 
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700" 
                  size="lg"
                  onClick={handleBookClick}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Book Charging Session
                </Button>
              ) : (
                <Button className="w-full" size="lg" variant="outline" disabled>
                  Currently Unavailable
                </Button>
              )}
              
              <div className="flex justify-center">
                <InterestButton stationId={stationId} stationTitle={`Station ${stationId}`} />
              </div>
            </div>
            
            <p className="text-xs text-center text-muted-foreground">
              You won't be charged until you complete your booking
            </p>
          </div>
        </CardContent>
      </Card>
    </ErrorBoundary>
  );
};

export default BookingSidebar;
